import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Divider, Space, Button, Tag } from 'antd';
import DashboardStatistics from 'components/DashboardStatistics';
import { CalendarOutlined, ShoppingCartOutlined, CloseCircleFilled, ExceptionOutlined } from '@ant-design/icons';
import { sum } from 'shared/constants/math';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import dayjs from 'dayjs';
import DataSourceTypeLabel from 'components/DataSourceTypeLabel';
import LicenseStatus from 'components/LicenseStatus';
import theme from 'shared/constants/theme';
import CenterContent from 'components/CenterContent';
import useUser from 'hooks/useUser';

const QUERY = gql`
  query DataSourceCard($id: ID!, $startDate: DateTime!, $endDate: DateTime!) {
    dataSource(id: $id) {
      id      
      website {
        id      
        matomoId
        visitStats(startDate: $startDate, endDate: $endDate) {
          date
          visits
          actions
          visitors
        }        
      }  
      surveyGroups(statsDateRanges:[{ startDate: $startDate, endDate: $endDate }]) {
        id
        viewCounts
        responseCounts
      }          
    }
  }
`;

function getDataSourceStatistics(dataSource) {
  const stats = [];
  if (dataSource?.website?.visitStats) {
    const { visitStats } = dataSource.website;
    stats.push(...[
      { title: 'Visitors', value: sum(visitStats, { valueSelector: v => v.visitors }), chartData: visitStats.map(s => s.visitors) },
      { title: 'Visits', value: sum(visitStats, { valueSelector: v => v.visits }), chartData: visitStats.map(s => s.visits) },
      { title: 'Page views', value: sum(visitStats, { valueSelector: v => v.actions }), chartData: visitStats.map(s => s.actions) },
    ]);
  }

  if (dataSource?.surveyGroups) {
    stats.push({ title: 'Survey views', value: sum(dataSource.surveyGroups, { valueSelector: v => v.viewCounts?.[0] }) });
    stats.push({ title: 'Survey responses', value: sum(dataSource.surveyGroups, { valueSelector: v => v.responseCounts?.[0] }) });
  }

  return stats;
}

const DataSourceCard = ({ dataSource, hasAccess, canEditLicense, isDemo }) => {
  const { isSuperUser } = useUser();
  const { data, loading } = useQuery(QUERY, {
    variables: {
      id: dataSource.id,
      startDate: dayjs().subtract(6, 'days').startOf('day').toDate(),
      endDate: dayjs().endOf('day').toDate(),
    },
    skip: !hasAccess || !dataSource.id,
  });

  let cardContent = null;
  let disabled = false;

  if (!hasAccess) {
    cardContent = (
      <CenterContent>
        <CloseCircleFilled style={{ color: theme['error-color'], fontSize: '36px' }} />
        <p>
          You don&apos;t have access to this data source.
        </p>
      </CenterContent>
    );
    disabled = true;
  } else if (!dataSource.activeLicense) {
    if (canEditLicense) {
      cardContent = (
        <CenterContent>
          <p>
            Buy a license to access the data collected
          </p>
          <Space>
            <Link to={`/data-sources/${dataSource.id}/settings/license`}>
              <Button type="primary" icon={<ShoppingCartOutlined />}>
                Buy new license
              </Button>
            </Link>
            {isSuperUser && (
            <Link to={`/data-sources/${dataSource.id}`}>
              <Button>
                View data source
              </Button>
            </Link>
            )}
          </Space>
        </CenterContent>
      );
    } else {
      cardContent = (
        <CenterContent>
          <ExceptionOutlined style={{ color: theme['error-color'], fontSize: '36px' }} />
          <p>
            A license is needed to access the data collected.
          </p>
        </CenterContent>
      );
    }
    disabled = true;
  } else {
    cardContent = <DashboardStatistics statistics={getDataSourceStatistics(data?.dataSource)} />;
  }

  const card = (
    <Card
      hoverable={!disabled}
      title={(
        <Space>
          {dataSource.title}
          {isDemo && <Tag color="red-inverse">Demo</Tag>}
        </Space>
      )}
      extra={(
        <Space split={<Divider type="vertical" />}>
          <DataSourceTypeLabel type={dataSource.type} />
          <Space>
            <LicenseStatus license={dataSource.activeLicense} />
          </Space>
          <Space>
            <CalendarOutlined />
            Last 7 days
          </Space>
        </Space>
      )}
      loading={loading}
      className={disabled ? 'disabled' : ''}
    >
      {cardContent}
    </Card>
  );

  if (!disabled) {
    return (
      <Link to={`/data-sources/${dataSource.id}`}>
        {card}
      </Link>
    );
  }

  return card;
};

export default DataSourceCard;
